import axios from 'axios'
import request from '@/config/axios'
import type { UploadService, UploadResult } from '@/components/rich-text-editor'

/**
 * 上传场景：首段是模块，/ 后是模块内子业务目录。
 * file-service 只按模块配置后缀，不限制大小/次数；
 * 按钮级后缀白名单由下方 UPLOAD_EXTS 在前端兜底，前端不限制文件大小。
 */
export const BIZ_PREFIX = {
  /** 通用附件（审批评论/表单上传等） */
  COMMON: 'common/attachment',
  /** 课程封面 */
  COVER: 'college/cover',
  /** 富文本插图 */
  IMAGE: 'college/image',
  /** 课程资料 */
  MATERIAL: 'college/material',
  /** 课时视频 */
  VIDEO: 'college/video',
  /** 证书自定义背景图 */
  CERT_BG: 'college/cert-bg',
  /** 渠道身份富文本插图 */
  CHANNEL_IMAGE: 'channel/image',
  /** 渠道政策视频 */
  CHANNEL_VIDEO: 'channel/video',
  /** 协议正文富文本媒体（图片/视频共用；签名时长按后缀自动区分） */
  AGREEMENT: 'identity/agreement'
} as const

export type BizPrefix = (typeof BIZ_PREFIX)[keyof typeof BIZ_PREFIX]

/** 按钮级后缀白名单（服务端按模块校验后缀；按钮级细分由前端兜底，前端不限制大小） */
export const UPLOAD_EXTS: Record<BizPrefix, string[]> = {
  [BIZ_PREFIX.COMMON]: [
    'jpg',
    'jpeg',
    'png',
    'webp',
    'gif',
    'bmp',
    'pdf',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'ppt',
    'pptx',
    'txt',
    'zip',
    'mp4'
  ],
  [BIZ_PREFIX.COVER]: ['jpg', 'jpeg', 'png', 'webp'],
  [BIZ_PREFIX.IMAGE]: ['jpg', 'jpeg', 'png', 'webp', 'gif'],
  [BIZ_PREFIX.MATERIAL]: ['pdf', 'ppt', 'pptx', 'doc', 'docx', 'xls', 'xlsx', 'zip'],
  [BIZ_PREFIX.VIDEO]: ['mp4', 'mov', 'm4v'],
  [BIZ_PREFIX.CERT_BG]: ['jpg', 'jpeg', 'png'],
  [BIZ_PREFIX.CHANNEL_IMAGE]: ['jpg', 'jpeg', 'png', 'webp', 'gif'],
  [BIZ_PREFIX.CHANNEL_VIDEO]: ['mp4', 'mov', 'm4v'],
  [BIZ_PREFIX.AGREEMENT]: ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'mp4', 'mov', 'm4v']
}

/** 报错用的业务名称（不向前端暴露内部前缀） */
const UPLOAD_LABEL: Record<BizPrefix, string> = {
  [BIZ_PREFIX.COMMON]: '附件',
  [BIZ_PREFIX.COVER]: '课程封面',
  [BIZ_PREFIX.IMAGE]: '图片',
  [BIZ_PREFIX.MATERIAL]: '课程资料',
  [BIZ_PREFIX.VIDEO]: '视频',
  [BIZ_PREFIX.CERT_BG]: '证书背景图',
  [BIZ_PREFIX.CHANNEL_IMAGE]: '图片',
  [BIZ_PREFIX.CHANNEL_VIDEO]: '视频',
  [BIZ_PREFIX.AGREEMENT]: '媒体'
}

/**
 * 上传前后缀校验：只限制后缀类型，不限制文件大小（服务端同样只校验后缀）
 * @param file 文件本体
 * @param bizPrefix 模块/子路径
 * @throws 后缀不在白名单时抛出可直接展示给用户的错误
 */
export const validateUploadFile = (file: File, bizPrefix: BizPrefix) => {
  const exts = UPLOAD_EXTS[bizPrefix]
  const label = UPLOAD_LABEL[bizPrefix] || '文件'
  if (file.size <= 0) {
    throw new Error(`${label}为空，请重新选择`)
  }
  const ext = fileExt(file.name)
  if (!ext || !exts.includes(ext)) {
    throw new Error(`${label}仅支持 ${exts.map((item) => item.toUpperCase()).join('、')} 格式`)
  }
}

/** 上传规则（后端按模块下发） */
export interface FileRuleVO {
  bizPrefix: string
  /** 允许的后缀列表 */
  exts: string[]
}

/** 上传结果（业务表单只存 key） */
export interface UploadedFileVO {
  /** 对象 key，如 college/cover/xxx.png */
  key: string
  /** 访问路径，可直接用作 img / video src */
  accessPath: string
}

/** 直传预签名信息 */
export interface PresignedVO {
  /** 对象存储预签名 PUT 地址，15 分钟内有效 */
  url: string
  key: string
  /** 签名有效期秒数 */
  ttlSeconds: number
  accessPath: string
}

/**
 * 查询上传规则（前端据此渲染格式提示）
 * @param bizPrefix 模块或模块/子路径；不传返回全部模块规则
 * @returns 模块规则列表
 */
export const getFileRules = async (bizPrefix?: BizPrefix) => {
  return await request.get<FileRuleVO[]>({ url: '/api/file/file/rules', params: { bizPrefix } })
}

/** 媒体上传超时：大文件走网关很慢，示例给 5 分钟 */
const MEDIA_UPLOAD_TIMEOUT = 300_000

/** 中转/直传通道选择阈值（仅传输方式，不是业务大小上限） */
const RELAY_UPLOAD_MAX_BYTES = 10 * 1024 * 1024

/**
 * 中转上传（建议 ≤ 10MB；大文件走直传，避免网关长连接）
 * @param file 文件本体
 * @param bizPrefix 模块/子路径
 * @param ext 后缀，不含点（如 png）
 * @returns 对象 key 与访问路径
 */
export const uploadFile = async (file: File, bizPrefix: BizPrefix, ext: string) => {
  const data = new FormData()
  data.append('file', file)
  data.append('bizPrefix', bizPrefix)
  data.append('ext', ext)
  return await request.upload<UploadedFileVO>({
    url: '/api/file/file/upload',
    data,
    timeout: MEDIA_UPLOAD_TIMEOUT
  })
}

/**
 * 申请直传地址（大文件走两步直传的第一步）
 * @param data 前缀、后缀与 MIME；第二步 PUT 的 Content-Type 必须与 contentType 一致
 * @returns 预签名 PUT 地址与对象 key
 */
export const presignFile = async (data: {
  bizPrefix: BizPrefix
  ext: string
  contentType: string
}) => {
  return await request.post<PresignedVO>({ url: '/api/file/file/presign', data })
}

/**
 * 直传第二步：PUT 文件二进制到预签名地址
 * 该地址是对象存储的绝对地址，不能走本项目 axios 实例（会带上 /api 前缀与鉴权头）
 * @param url 预签名 PUT 地址
 * @param file 文件本体
 * @param contentType 与 presign 请求一致的 MIME
 */
const putDirect = async (url: string, file: File, contentType: string) => {
  await axios.put(url, file, { headers: { 'Content-Type': contentType } })
}

/** 取文件后缀（不含点，小写） */
export const fileExt = (name: string) => (name.split('.').pop() || '').toLowerCase()

/** 展示签名结果 */
export interface SignedFileVO {
  key: string
  /** 对象存储直连地址（不要再拼网关前缀） */
  url: string
  ttlSeconds: number
  /** 过期毫秒时间戳 */
  expiresAt: number
}

/** 存库态的富文本文件地址前缀（表单与富文本内容里存的一律是这个形态） */
export const FILE_PATH_PREFIX = '/api/file/file/'

/** 对象 key 形态：module[/子路径...]/32位hex.ext */
const KEY_RE = /^(?:[A-Za-z0-9_-]+\/)+[0-9a-f]{32}\.[a-z0-9]{1,10}$/i

/**
 * 只有本站存储签出的地址才允许按 path 反推 key：默认本站 COS 桶（region 可变）与本地文件网关；
 * 换桶/CDN 域名或本地改用 LAN IP 时用 VITE_STORAGE_HOSTS 追加（逗号分隔 host[:port]）。
 * 外链（第三方图床等）一律原样保留——否则形如 /05/xxxx.png 的外链会被误改写成 /api/file 地址。
 */
const OWN_COS_HOST = /^juan-1311262694\.cos\.[a-z0-9-]+\.myqcloud\.com$/i
const DEFAULT_LOCAL_HOSTS = ['localhost:10014', '127.0.0.1:10014']
const ENV_STORAGE_HOSTS = ((import.meta.env.VITE_STORAGE_HOSTS as string | undefined) || '')
  .split(',')
  .map((host) => host.trim())
  .filter(Boolean)
const ALLOWED_STORAGE_HOSTS = [...DEFAULT_LOCAL_HOSTS, ...ENV_STORAGE_HOSTS]

/** 是否本站存储地址（按 URL 解析取 host 判定，不做路径正则匹配） */
const isStorageUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return false
    }
    return OWN_COS_HOST.test(parsed.host) || ALLOWED_STORAGE_HOSTS.includes(parsed.host)
  } catch {
    return false
  }
}

/**
 * 从本站存储签名 URL 反推对象 key。
 * COS 签名 URL 的 path 就是 key；本地文件网关带 /file 或 /file/raw 前缀，先剥离。
 * @param url 签名后的直连地址
 * @returns 合法对象 key；不是本站存储或路径非法时返回 null（调用方保留原地址）
 */
const storageKeyOf = (url: string): string | null => {
  try {
    const parsed = new URL(url)
    if (!isStorageUrl(url)) {
      return null
    }
    let path = decodeURIComponent(parsed.pathname).replace(/^\/+/, '')
    if (!OWN_COS_HOST.test(parsed.host)) {
      path = path.replace(/^file\/raw\//, '').replace(/^file\//, '')
    }
    return KEY_RE.test(path) ? path : null
  } catch {
    return null
  }
}

/** 签名 URL 缓存：key → { url, expiresAt }；提前 1 分钟视为过期，避免边界失效 */
const urlCache = new Map<string, { url: string; expiresAt: number }>()
/** 反向索引：签名 URL → key（保存前要把地址换回 key） */
const urlToKey = new Map<string, string>()
const EXPIRY_BUFFER = 60_000

/** 单次签名的 key 上限（接口约定） */
const SIGN_BATCH = 100

/**
 * 批量换取文件的展示签名 URL（登录即可；内部按 100 个一批自动分批）
 * 命中的缓存直接复用，只对未命中或临近过期的 key 发请求
 * @param keys 对象 key 数组
 * @returns key → 签名 URL 的映射（换取失败的 key 不在其中）
 */
export const signFileUrls = async (keys: string[]) => {
  const unique = [...new Set(keys.filter(Boolean))]
  const need = unique.filter((key) => {
    const hit = urlCache.get(key)
    return !hit || hit.expiresAt <= Date.now() + EXPIRY_BUFFER
  })
  for (let i = 0; i < need.length; i += SIGN_BATCH) {
    const items = await request.post<SignedFileVO[]>({
      url: '/api/file/file/sign',
      data: { keys: need.slice(i, i + SIGN_BATCH) }
    })
    ;(items || []).forEach((item) => {
      urlCache.set(item.key, { url: item.url, expiresAt: item.expiresAt })
      urlToKey.set(item.url, item.key)
    })
  }
  const result: Record<string, string> = {}
  unique.forEach((key) => {
    const hit = urlCache.get(key)
    if (hit) result[key] = hit.url
  })
  return result
}

/**
 * 单个文件的展示 URL（带缓存，临近过期自动重新签名）
 * 图片签名 10 分钟、视频 2 小时，页面停留超时后这里会自动换新
 * @param key 对象 key
 * @returns 可用的签名 URL；key 为空时返回空串
 * @throws 换取失败时抛错，由调用方决定兜底（预览类可置空，上传类应报给用户）
 */
export const resolveFileUrl = async (key: string) => {
  if (!key) return ''
  const hit = urlCache.get(key)
  if (hit && hit.expiresAt > Date.now() + EXPIRY_BUFFER) return hit.url
  const map = await signFileUrls([key])
  return map[key] || ''
}

/** 签名 URL → 对象 key（未收录的返回 null） */
export const fileKeyOf = (url: string) => urlToKey.get(url) || null

/**
 * 存库地址 → 可展示地址。
 *
 * 库里可能存 `/api/file/file/{key}`（表单上传）或纯 key（评论附件）；
 * 直接把它作为 a/img 地址不会带 Authorization，网关会 401，必须先换签名 URL。
 * 完整 http(s) 地址原样返回。
 */
export const resolveStoredFileUrl = async (value: string): Promise<string> => {
  if (!value) return ''
  if (/^https?:\/\//i.test(value)) return value
  const key = value.startsWith(FILE_PATH_PREFIX) ? value.slice(FILE_PATH_PREFIX.length) : value
  try {
    return await resolveFileUrl(key)
  } catch {
    return value
  }
}

/** 富文本 HTML 里出现的所有存库态文件 key */
const keysInHtml = (html: string) => [
  ...new Set([...html.matchAll(/\/api\/file\/file\/([^"'\s)]+)/g)].map((m) => m[1]))
]

/**
 * 富文本 HTML → 展示态：把 /api/file/file/{key} 全部换成签名 URL
 * 必须做这一步：编辑器/预览的清洗只放行 https 的 img/video，相对路径会被整张丢弃
 * @param html 存库态 HTML
 * @returns 展示态 HTML（换取失败的部分保持原样）
 */
export const toDisplayHtml = async (html: string) => {
  if (!html) return ''
  const keys = keysInHtml(html)
  if (!keys.length) return html
  try {
    const map = await signFileUrls(keys)
    return html.replace(
      /\/api\/file\/file\/([^"'\s)]+)/g,
      (whole, key: string) => map[key] || whole
    )
  } catch {
    // 换取失败：保留存库态原样，宁可图不显示也不能让内容被清掉
    return html
  }
}

/**
 * 富文本 HTML → 存库态：把签名 URL 全部换回 /api/file/file/{key}
 * 保存/自动保存前必须调用，否则会把会过期的签名地址写进库
 *
 * <p>注意：编辑器序列化会把查询串里的 &amp; 转成 &amp;，查表前必须先解码；
 * 未命中签名缓存时按对象存储 path 中的 bizPrefix/32位hex.ext 反推 key 兜底。
 * @param html 展示态 HTML
 * @returns 存库态 HTML
 */
export const toStoredHtml = (html: string) => {
  if (!html) return ''
  return html.replace(/https?:\/\/[^"'\s)]+/g, (rawUrl) => {
    const decoded = rawUrl.replace(/&amp;/gi, '&').replace(/&#38;/g, '&')
    const key = urlToKey.get(rawUrl) || urlToKey.get(decoded)
    if (key) {
      return FILE_PATH_PREFIX + key
    }
    // 仅本站存储域才反推 key；外链保持原样，避免误伤合法外链
    const inferred = storageKeyOf(decoded)
    return inferred ? FILE_PATH_PREFIX + inferred : rawUrl
  })
}

/**
 * 浏览器报不出 MIME 时的按后缀兜底（`.mov` / `.m4v` 在部分系统上 `file.type` 为空串）
 * 直传的 Content-Type 必须与 presign 时一致，空串会让后端判成类型不匹配
 */
const MIME_BY_EXT: Record<string, string> = {
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  m4v: 'video/x-m4v'
}

/**
 * 按文件大小选传输通道（≤10MB 走中转，>10MB 走 presign + PUT 直传）。
 * 10MB 只是传输方式阈值，不限制文件大小；业务不设大小上限。
 *
 * ⚠️ 直传是浏览器**直连对象存储**，需要该桶放通本站点的跨域（CORS）。
 * 桶没配时预检请求直接 403，PUT 根本没发出去 —— 此时**不退回中转通道**：
 * 网关对超大请求会挂很久才失败，比立刻说清原因更糟。
 * @param file 文件本体
 * @param bizPrefix 模块/子路径
 * @returns 对象 key 与访问路径
 */
export const uploadBySize = async (file: File, bizPrefix: BizPrefix) => {
  validateUploadFile(file, bizPrefix)
  const ext = fileExt(file.name)
  const contentType = file.type || MIME_BY_EXT[ext] || ''
  if (file.size <= RELAY_UPLOAD_MAX_BYTES) {
    return await uploadFile(file, bizPrefix, ext)
  }
  try {
    const presigned = await presignFile({ bizPrefix, ext, contentType })
    await putDirect(presigned.url, file, contentType)
    return { key: presigned.key, accessPath: presigned.accessPath }
  } catch (directError) {
    console.error('[file] 直传失败', directError)
    // 上传失败只透出后端的原始提示，不再附加通道说明
    const raw = directError instanceof Error ? directError.message : String(directError)
    throw new Error(raw || '上传失败')
  }
}

/** 富文本里允许的视频后缀（与 college 模块白名单一致） */
const VIDEO_EXT = /\.(mp4|mov|m4v)$/i

/**
 * 富文本编辑器的媒体上传适配器：**按文件类型分派场景前缀**
 * - 图片 → `college/image`
 * - 视频 → `college/video`
 *
 * 大小通道由 `uploadBySize` 自动选：≤10MB 走中转，>10MB 走 presign + PUT 直传。
 * 编辑器要立刻能显示，故返回签名 URL；保存时由 `toStoredHtml` 换回对象 key（落库只存相对路径）。
 */
export class CollegeUploadService implements UploadService {
  async upload(file: File): Promise<UploadResult> {
    const isVideo = (file.type || '').startsWith('video/') || VIDEO_EXT.test(file.name)
    const res = await uploadBySize(file, isVideo ? BIZ_PREFIX.VIDEO : BIZ_PREFIX.IMAGE)
    const url = await resolveFileUrl(res.key)
    if (!url) throw new Error(`${isVideo ? '视频' : '图片'}已上传但换取展示地址失败，请重试`)
    return { url, alt: file.name }
  }
}
